import { useLayoutEffect } from "react";
import { CustomerStyleService } from "../../Services/customerStyleService";

/**
 * Customer-specific CSS for the pre-login pages (sign-in, password reset, shared
 * views, communication settings), resolved by the backend from the visitor's
 * origin (customer parameter CUSTOM_STYLING).
 *
 * Same idea as applyBrowserBranding: cache the answer per origin in localStorage so
 * repeat visits paint correctly on the very first frame, and on a cold visit let the
 * AppLoader wait (capped) for the answer before rendering anything — so a visitor of
 * a white-label URL never sees the default Dashly sign-in page flash by.
 *
 * The stylesheet is only injected while a page that opts in (via the hook) is
 * mounted: the CSS is written for the pre-login pages and must not leak into the
 * logged-in application.
 */

const STYLE_ELEMENT_ID = "dashly-url-specific-styling";
const CACHE_KEY_PREFIX = "dashly.urlStyling:";
/** Longest a cold first visit waits for the backend before showing the page anyway. */
export const URL_STYLING_FIRST_LOAD_TIMEOUT_MS = 1500;

/** Latest answer from the backend in this session; `undefined` = not received yet. */
let resolvedStyling: string | null | undefined;
let inFlight: Promise<string | null | undefined> | null = null;

const cacheKey = (): string => `${CACHE_KEY_PREFIX}${window.location.origin}`;

/** `null` = nothing cached (unknown); `""` = cached as "no custom styling". */
export const readUrlSpecificStylingCache = (): string | null => {
	try {
		return window.localStorage.getItem(cacheKey());
	} catch {
		return null;
	}
};

export const writeUrlSpecificStylingCache = (styling: string | null): void => {
	try {
		window.localStorage.setItem(cacheKey(), styling ?? "");
	} catch {
		// localStorage may be unavailable
	}
};

export const clearUrlSpecificStylingCache = (): void => {
	try {
		window.localStorage.removeItem(cacheKey());
	} catch {
		// localStorage may be unavailable
	}
	resolvedStyling = undefined;
	inFlight = null;
};

/** Best known value right now, without a network round trip. */
export const currentUrlSpecificStyling = (): string | null => {
	if (resolvedStyling !== undefined) {
		return resolvedStyling;
	}
	const cached = readUrlSpecificStylingCache();
	return cached ? cached : null;
};

export const applyUrlSpecificStyling = (styling: string | null): void => {
	let element = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
	if (!styling) {
		element?.remove();
		return;
	}
	if (!element) {
		element = document.createElement("style");
		element.id = STYLE_ELEMENT_ID;
		document.head.appendChild(element);
	}
	if (element.textContent !== styling) {
		element.textContent = styling;
	}
};

/**
 * Fetches (once per page load, shared by all callers) and updates the cache.
 * Resolves to `undefined` when the request failed, so callers keep whatever they had.
 */
export const fetchUrlSpecificStyling = (): Promise<string | null | undefined> => {
	if (!inFlight) {
		inFlight = new CustomerStyleService()
			.fetchUrlSpecificStyling()
			.then((response) => {
				const data = response.data?.data;
				const styling = typeof data === "string" && data.trim() ? data : null;
				resolvedStyling = styling;
				writeUrlSpecificStylingCache(styling);
				return styling;
			})
			.catch(() => undefined);
	}
	return inFlight;
};

/**
 * Called by the AppLoader before the first render. With a cache the answer is
 * already known (the fetch refreshes it in the background); on a cold visit this
 * waits for the backend, but never longer than the timeout.
 */
export const prepareUrlSpecificStyling = async (): Promise<void> => {
	if (readUrlSpecificStylingCache() !== null) {
		void fetchUrlSpecificStyling();
		return;
	}
	const timeout = new Promise<undefined>((resolve) => {
		setTimeout(() => resolve(undefined), URL_STYLING_FIRST_LOAD_TIMEOUT_MS);
	});
	await Promise.race([fetchUrlSpecificStyling(), timeout]);
};

/**
 * For pages that should carry the customer's CSS: injects the best known value
 * synchronously before paint, swaps in the fresh value when the fetch completes,
 * and removes the stylesheet again on unmount.
 */
export const useUrlSpecificStyling = (): void => {
	useLayoutEffect(() => {
		let active = true;
		applyUrlSpecificStyling(currentUrlSpecificStyling());
		void fetchUrlSpecificStyling().then((styling) => {
			if (active && styling !== undefined) {
				applyUrlSpecificStyling(styling);
			}
		});
		return () => {
			active = false;
			applyUrlSpecificStyling(null);
		};
	}, []);
};
